/**
 * GraspPod — Brand icon components
 *
 * Mirrors the GS GraspIcons.tsx API shape (size / active / className) but uses
 * a per-instance React.useId() gradient ID so multiple icon instances on the
 * same page never collide on DOM IDs.
 *
 * Source-of-truth visual: public/favicon.svg
 *   - viewBox 0 0 32 32
 *   - Open arc (M16 2 → A14 14 0 0 0 2 16 → A14 14 0 0 0 16 30) stroke 4, round caps
 *   - Center dot (cx 16, cy 16, r 4)
 *   - Gradient #A78BFA (violet) → #7DD3FC (sky), diagonal 0,0 → 32,32
 */
import { useId } from 'react'

const INACTIVE_COLOR = 'var(--color-gray-600)'

interface GraspPodIconProps {
  size?: number
  active?: boolean
  className?: string
}

/** GP brand mark — violet→sky open-arc-with-center-dot, matches favicon.svg */
export function GraspPodIvyIcon({
  size = 24,
  active = false,
  className,
}: GraspPodIconProps) {
  // useId() yields a stable, instance-unique string we sanitize for SVG ID use.
  const rawId = useId()
  const gradId = `grasp-pod-ivy-${rawId.replace(/:/g, '')}`
  const paint = active ? `url(#${gradId})` : INACTIVE_COLOR

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradId}
          x1="0"
          y1="0"
          x2="32"
          y2="32"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#A78BFA" />
          <stop offset="100%" stopColor="#7DD3FC" />
        </linearGradient>
      </defs>
      <path
        d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14"
        stroke={paint}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="16" cy="16" r="4" fill={paint} />
    </svg>
  )
}
